import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  fetchOrderConversation,
  sendBulkWhatsAppTemplate,
  sendCompleteOrderPromptTemplate,
  sendDepositInfoTemplate,
  sendPrimaryPhoneConfirmation,
  sendPrimaryPhoneManualTemplate,
  sendSecondaryPhoneConfirmation,
} from "../../api/OrderAPIs";

const Svg = ({ size = 16, className, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);
const MessagesSquare = (p) => (
  <Svg {...p}>
    <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
    <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
  </Svg>
);
const RefreshCw = (p) => (
  <Svg {...p}>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </Svg>
);
const X = (p) => (
  <Svg {...p}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Svg>
);
const ImageIcon = (p) => (
  <Svg {...p}>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </Svg>
);
const AlertCircle = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </Svg>
);
const Send = (p) => (
  <Svg {...p}>
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </Svg>
);
const ChevronUp = (p) => (
  <Svg {...p}>
    <path d="m18 15-6-6-6 6" />
  </Svg>
);
const ChevronDown = (p) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

const isValidPhone = (phone) =>
  phone && phone !== "Unknown Phone" && phone !== "غير معروف";

const START_CHAT_ID = 83264;

/** Page-specific template actions shown under "ارسال رساله". */
export const CONVERSATION_TEMPLATE_PRESETS = {
  new: [
    {
      id: "manual-confirm",
      label: "اضغط علي تأكيد الطلب",
      mode: "order",
      send: (orderId) => sendPrimaryPhoneManualTemplate(orderId),
    },
    {
      id: "start-chat",
      label: "بدأ محادثه",
      mode: "phone",
      templateId: START_CHAT_ID,
    },
    {
      id: "confirm-secondary",
      label: "رساله التأكيد للرقم التاني",
      mode: "order",
      send: (orderId) => sendSecondaryPhoneConfirmation(orderId),
    },
    {
      id: "confirm-primary",
      label: "رساله التأكيد للرقم الاساسي",
      mode: "order",
      send: (orderId) => sendPrimaryPhoneConfirmation(orderId),
    },
  ],
  deposit: [
    {
      id: "complete-order",
      label: "حابب تكمل الاوردر",
      mode: "order",
      send: (orderId) => sendCompleteOrderPromptTemplate(orderId),
    },
    {
      id: "start-chat",
      label: "بدأ محادثه",
      mode: "phone",
      templateId: START_CHAT_ID,
    },
    {
      id: "deposit-25",
      label: "واتساب دفعة 25%",
      mode: "order",
      send: (orderId) => sendDepositInfoTemplate(orderId),
    },
  ],
  custom: [
    {
      id: "complete-order",
      label: "حابب تكمل الاوردر",
      mode: "order",
      send: (orderId) => sendCompleteOrderPromptTemplate(orderId),
    },
    {
      id: "start-chat",
      label: "بدأ محادثه",
      mode: "phone",
      templateId: START_CHAT_ID,
    },
    {
      id: "deposit-25",
      label: "واتساب دفعة 25%",
      mode: "order",
      send: (orderId) => sendDepositInfoTemplate(orderId),
    },
  ],
  hold: [
    { id: "merge", label: "دمج الطلبات", mode: "phone", templateId: 83262 },
    { id: "refused", label: "استفسار عن رفض الاستلام", mode: "phone", templateId: 83261 },
    { id: "address", label: "توضيح العنوان", mode: "phone", templateId: 83263 },
    { id: "design", label: "توضيح التصميم", mode: "phone", templateId: 83260 },
    { id: "start-chat", label: "بدأ الشات (عشان مناخدش بان)", mode: "phone", templateId: START_CHAT_ID },
  ],
};

const SEARCH_GROUPS = [
  {
    title: "تأكيد الطلب",
    items: CONVERSATION_TEMPLATE_PRESETS.new,
  },
  {
    title: "عربون / تصميم خاص",
    items: CONVERSATION_TEMPLATE_PRESETS.deposit,
  },
  {
    title: "معلق",
    items: CONVERSATION_TEMPLATE_PRESETS.hold,
  },
];

const formatTimestamp = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("ar-EG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const isImageUrl = (url, mimeType) => {
  if (mimeType && mimeType.startsWith("image/")) return true;
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url);
};

const normalizeConversation = (result) => ({
  found: result?.found ?? result?.Found ?? false,
  conversationId: result?.conversationId ?? result?.ConversationId,
  contactName: result?.contactName ?? result?.ContactName,
  contactPhone: result?.contactPhone ?? result?.ContactPhone,
  orderPhone: result?.orderPhone ?? result?.OrderPhone,
  conversationIdCached: result?.conversationIdCached ?? result?.ConversationIdCached,
  error: result?.error ?? result?.Error,
  messages: (result?.messages || result?.Messages || []).map((m) => ({
    id: m.id ?? m.Id,
    body: m.body ?? m.Body ?? "",
    direction: m.direction ?? m.Direction ?? "unknown",
    messageType: m.messageType ?? m.MessageType ?? "text",
    status: m.status ?? m.Status,
    mediaUrl: m.mediaUrl ?? m.MediaUrl,
    mimeType: m.mimeType ?? m.MimeType,
    caption: m.caption ?? m.Caption,
    createdAt: m.createdAt ?? m.CreatedAt,
    agentName: m.agentName ?? m.AgentName,
  })),
});

const TemplateActionButton = ({ label, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full text-right bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 text-slate-800 px-3 py-2.5 rounded-xl text-sm font-medium transition disabled:opacity-50"
  >
    {label}
  </button>
);

const ConversationModal = ({
  order,
  phone,
  templatePreset = "new",
  canSend = true,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [showSendPanel, setShowSendPanel] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const messagesContainerRef = useRef(null);

  const targetPhone = phone || order?.phoneNumber;

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const result = await fetchOrderConversation(order.orderNumber, {
        refresh,
        phone: targetPhone,
      });
      const normalized = normalizeConversation(result);
      setData(normalized);
      if (!normalized.found) {
        setError(normalized.error || "لا توجد محادثة BeOn لهذا العميل");
      }
    } catch (err) {
      setData(null);
      setError(err.message || "فشل تحميل المحادثة");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.orderNumber, targetPhone]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    if (loading || !data?.found) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [loading, data]);

  const handleSendTemplate = async (action) => {
    if (!canSend || !action) return;

    const confirmed = window.confirm(
      `إرسال «${action.label}»؟\n\nالرقم: ${targetPhone}\nطلب #${order.orderNumber}`
    );
    if (!confirmed) return;

    setSendingId(action.id);
    try {
      let ok = false;
      if (action.mode === "phone") {
        const result = await sendBulkWhatsAppTemplate(action.templateId, targetPhone);
        ok = (result?.processed ?? 0) > 0;
        if (!ok) {
          const failed = result?.results?.find((r) => !r.success);
          throw new Error(failed?.error || "فشل إرسال القالب");
        }
      } else {
        const result = await action.send(order.orderNumber);
        ok = result?.success !== false;
        if (result?.success === false) {
          throw new Error(result?.error || "فشل إرسال القالب");
        }
      }

      toast.success(`تم إرسال «${action.label}»`);
      setTimeout(() => load(true), 1200);
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingId(null);
    }
  };

  const messages = data?.messages || [];
  const contactLabel = data?.contactName || order.name || "العميل";
  const phoneLabel = data?.contactPhone || data?.orderPhone || targetPhone;
  const presetKey = templatePreset === "custom" ? "custom" : templatePreset;
  const isSearch = presetKey === "search";

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="محادثة BeOn"
      >
        <div className="bg-slate-800 text-white px-4 py-3 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-300">محادثة BeOn</p>
            <p className="font-bold text-lg truncate">{contactLabel}</p>
            <p className="text-sm text-slate-300 tracking-wide truncate" dir="ltr">
              {phoneLabel}
            </p>
            {data?.conversationId ? (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Conversation #{data.conversationId}
                {data.conversationIdCached ? " · محفوظ" : " · تم البحث"}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => load(true)}
              disabled={loading || refreshing}
              title="إعادة البحث عن المحادثة"
              className="p-2 rounded-xl hover:bg-white/10 disabled:opacity-50 transition"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/10 transition"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto bg-slate-100 px-3 py-4 min-h-[220px]"
        >
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 py-16">
              <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm">جاري تحميل المحادثة...</p>
            </div>
          ) : error && !data?.found ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-4 py-16">
              <AlertCircle className="text-amber-500" size={36} />
              <p className="text-slate-700 font-medium">{error}</p>
              <button
                type="button"
                onClick={() => load(true)}
                className="mt-2 text-sm bg-slate-800 text-white px-4 py-2 rounded-xl hover:bg-slate-700"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-500 py-16 text-sm">
              لا توجد رسائل في هذه المحادثة
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {messages.map((msg) => {
                const outgoing = msg.direction === "outgoing";
                return (
                  <div
                    key={msg.id}
                    className={`flex ${outgoing ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 shadow-sm ${
                        outgoing
                          ? "bg-white text-slate-800 rounded-br-md border border-slate-200"
                          : "bg-emerald-600 text-white rounded-bl-md"
                      }`}
                    >
                      {outgoing && msg.agentName ? (
                        <p className="text-[10px] font-semibold text-slate-400 mb-1">
                          {msg.agentName}
                        </p>
                      ) : null}
                      {msg.mediaUrl ? (
                        <div className="mb-1.5">
                          {isImageUrl(msg.mediaUrl, msg.mimeType) ? (
                            <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={msg.mediaUrl}
                                alt={msg.caption || "مرفق"}
                                className="rounded-lg max-h-48 max-w-full object-contain"
                              />
                            </a>
                          ) : (
                            <a
                              href={msg.mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1.5 text-sm underline ${
                                outgoing ? "text-blue-600" : "text-emerald-100"
                              }`}
                            >
                              <ImageIcon size={14} />
                              فتح المرفق
                            </a>
                          )}
                        </div>
                      ) : null}
                      {msg.body ? (
                        <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                          {msg.body}
                        </p>
                      ) : null}
                      <div
                        className={`mt-1.5 flex items-center gap-2 text-[10px] ${
                          outgoing ? "text-slate-400" : "text-emerald-100/90"
                        }`}
                      >
                        <span>{formatTimestamp(msg.createdAt)}</span>
                        {msg.messageType && msg.messageType !== "text" ? (
                          <span className="opacity-80">· {msg.messageType}</span>
                        ) : null}
                        {msg.status ? <span className="opacity-80">· {msg.status}</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {error && data?.found ? (
          <div className="px-4 py-2 bg-amber-50 text-amber-800 text-xs border-t border-amber-100 shrink-0">
            {error}
          </div>
        ) : null}

        {canSend ? (
          <div className="border-t border-slate-200 bg-white shrink-0">
            {showSendPanel ? (
              <div className="px-3 pt-3 pb-2 max-h-[38vh] overflow-y-auto space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-slate-700">اختر الرسالة</p>
                  <button
                    type="button"
                    onClick={() => setShowSendPanel(false)}
                    className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                  >
                    إخفاء
                    <ChevronDown size={14} />
                  </button>
                </div>

                {isSearch ? (
                  SEARCH_GROUPS.map((group) => (
                    <div key={group.title} className="space-y-1.5">
                      <p className="text-[11px] font-semibold text-slate-500 px-1">{group.title}</p>
                      {group.items.map((action) => (
                        <TemplateActionButton
                          key={`${group.title}-${action.id}`}
                          label={action.label}
                          disabled={!!sendingId}
                          onClick={() => handleSendTemplate(action)}
                        />
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="space-y-1.5">
                    {(CONVERSATION_TEMPLATE_PRESETS[presetKey] || []).map((action) => (
                      <TemplateActionButton
                        key={action.id}
                        label={sendingId === action.id ? "جاري الإرسال..." : action.label}
                        disabled={!!sendingId}
                        onClick={() => handleSendTemplate(action)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div className="px-3 py-2.5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-500">
                {data?.found ? `${messages.length} رسالة` : "—"} · طلب #{order.orderNumber}
              </span>
              <button
                type="button"
                onClick={() => setShowSendPanel((v) => !v)}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-2 rounded-xl transition"
              >
                <Send size={14} />
                ارسال رساله
                {showSendPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-4 py-2.5 bg-white border-t border-slate-200 text-[11px] text-slate-500 flex justify-between shrink-0">
            <span>{data?.found ? `${messages.length} رسالة` : "—"}</span>
            <span>طلب #{order.orderNumber}</span>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

const ViewConversationButton = ({
  order,
  phone,
  templatePreset = "new",
  className = "",
  label = "عرض المحادثة",
  hidden = false,
  compact = false,
  canSend = true,
}) => {
  const [open, setOpen] = useState(false);
  const targetPhone = useMemo(
    () => phone || order?.phoneNumber,
    [phone, order?.phoneNumber]
  );

  if (hidden || !isValidPhone(targetPhone)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`عرض محادثة BeOn — ${targetPhone}`}
        className={
          compact
            ? `bg-slate-700 text-white px-2.5 py-1.5 rounded-lg hover:bg-slate-800 text-xs font-medium transition-all ${className}`
            : `bg-slate-700 text-white px-3 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 text-sm font-medium transition-all ${className}`
        }
      >
        <div className="flex items-center justify-center gap-1">
          <MessagesSquare size={compact ? 12 : 14} />
          {label}
        </div>
      </button>
      {open ? (
        <ConversationModal
          order={order}
          phone={targetPhone}
          templatePreset={templatePreset}
          canSend={canSend}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ViewConversationButton;
